'use strict';
/* ==========================================================================
   levels/song.js — step "song" (any): THE SONG, "Hidden in the Pod" (indie folk, DESIGN.md §5.4) as a botanical-plate
   collage with karaoke. The machinery (song clock, <audio> stream, karaoke band, portrait dock, controls, completion)
   is lessons/branches/levels/march.js's; the stage is this lesson's.

   ► The song: assets/music/song.m4a = ACE-Step take indiefolk_b104d96_7 (104 bpm sheet, 96 s; assets/CHOICE.md).
     Timings: assets/music/song_words.json (tools/song_timing.py): the TRUE lyric lines of content/song.json with word
     times from the take's Whisper passes, and the beat grid {bpm 103, offset = first downbeat ≈ 1.90 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a sung word (S.W(line, word)); every genotype, box, percent and count comes from core-local/cross.js (nums()).

   Always on: a pea vine grows along the potting bench from a potted pea plant, its tip keeping pace with the song
   (so it doubles as progress); one leaf pops on every beat, a tendril curl on every downbeat, pods hang on the
   chorus' "pair", flowers bloom at the end.
   Acts, keyed to lyric-line indices (content/song.json order):
     verse1   0–3    the family card, labelled SIMPLIFIED MODEL: Mom and Dad brown-eyed (Bb), Lily blue-eyed; "one from
                     my mother, and one from my dad": one b from each slides down into Lily's pair (bb → blue: the
                     model's Cross.square('Bb', 'Bb') box); "a pair": the gene pair, one allele from each parent
     chorus   4–7, 14–17   allele tiles: the big Y slides over the little y on "hides"; on "still there" the little y
                     peeks out (Yy → yellow, Cross.phenotype); HIDDEN · NOT GONE; on "riding along" the pair rides a
                     pea pod along the bench; "one from each parent in every pair": two parent pairs (Yy × Yy) split
                     and one tile from each drops into the child's pair (chorus 1 Y + y = Yy yellow, chorus 2 y + y =
                     yy green: both are boxes of Cross.square('Yy', 'Yy'))
     verse2   8–11   the wooden seed tray (Plate.tray, Yy × Yy): a box fills on each of "four boxes to fill"; "one in
                     four is green" rings the yy box (25%), "three are yellow" ticks the rest (75%) — Cross.phenotypes;
                     "every seed's a new draw": a row of 8 seeds from the seeded simulation Cross.grow; "the pod keeps
                     no score": a struck-out tally; "twenty-five percent": 25% (Cross.percent)
     bridge   12–13  EYE COLOR = A TEAM OF GENES: a cluster of gene tiles round an eye (labelled simplified, letters
                     made up); "but the peas keep it simple": ONE gene, Y / y; "Mendel": his real totals from
                     Cross.MENDEL (6,022 yellow : 2,001 green)
     outro    18–19  "Hidden, not gone" twice: the big Y hides the little y, the little y pops back out; flowers bloom;
                     INHERITED + a wax seal
   Karaoke (bottom band, torn cream paper): the current line in slab type, the sung word on a mustard scrap, trait words
   in their colour (green, yellow, blue-eyed, brown-eyed), the hook "Hidden, not gone" in ransom letters, the next line
   faint below.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The song's end (≥ 90 % heard) → Game.complete('song').
   Portrait phone: the stage on top, Dock.karaoke + ↺ ▶ ⌂ + the strip below.
   Long music (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared
   <audio> element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan.acts, sc.debugAt(t) (freeze at song time t), sc.now(),
   sc.play(t), sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub() (stamp now),
   sc.lyricAt(t), sc.activeWord(t), sc.actAt(t), sc.vineAt(t) ({tip, leaves}), sc.heard, sc.ended, sc.audioReady;
   Levels.song.nums() (every number / genotype the stage shows, from Cross).
   ========================================================================== */
(function () {
  const STEP = 'song';
  const TAKE_NAME = 'indiefolk_b104d96_7';
  const AUDIO = 'music/song.m4a', WORDS = 'music/song_words.json';
  const KEY = 'song:pod';
  const WOOD = "'Alfa Slab One'", HANDF = "'Caveat Brush'", TYPE = "'Special Elite'";
  const PC = () => window.Plate.C;
  const BAND_Y = 832, CUR_Y = 912, NEXT_Y = 1012, CUR_SIZE = 56, NEXT_SIZE = 36;
  const BENCH_Y = 752;                                  // the vine's rail
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the song heard for the stamp / "Finish"
  const CREDIT = 'AI-generated song · the genetics is real';
  const YELLOW_TEXT = '#8a6a0c';                      // pea yellow, dark enough to read as text on cream

  /* ---------------- song timing ---------------- */
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 96;
      const b = d.beats || {};
      this.bpm = b.bpm || 104; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off ? 0 : Math.exp(-this.sinceBeat(t) * k); }
  }

  /* ---------------- every number and genotype on the stage: the model only ---------------- */
  let NUMS = null;
  function nums() {
    if (NUMS) return NUMS;
    const X = window.Cross;
    // verse 1: the story's family in the SIMPLIFIED eye model (both parents Bb, brown; Lily = the bb box, blue)
    const fam = X.square('Bb', 'Bb'), lilyBox = fam.boxes.find(b => b.phenotype === X.TRAITS.eyeModel.recessive);
    // the chorus + verse 2: Mendel's seed colour, Yy × Yy
    const sq = X.square('Yy', 'Yy'), ph = X.phenotypes('Yy', 'Yy');
    const g1 = X.gametes('Yy'), g2 = X.gametes('Yy');
    const cap = g1.find(a => a === a.toUpperCase()), low = g1.find(a => a !== a.toUpperCase());
    const kid = (a, b) => { const gt = X.norm(a + b); return { from: [a, b], genotype: gt, phenotype: X.phenotype('seedColour', gt), inSquare: sq.boxes.some(x => x.genotype === gt) }; };
    const M = X.MENDEL.find(m => m.trait === 'seedColour');
    NUMS = {
      eye: { p1: fam.p1, p2: fam.p2, parent: X.phenotype('eyeModel', fam.p1), lily: lilyBox.genotype, lilyFrom: [lilyBox.top, lilyBox.side], lilyPheno: lilyBox.phenotype, simplified: !!X.TRAITS.eyeModel.simplified },
      yy: { p1: sq.p1, p2: sq.p2, top: sq.top, side: sq.side, boxes: sq.boxes.map(b => b.genotype), pheno: sq.boxes.map(b => b.phenotype),
        green: ph.green, yellow: ph.yellow, greenBoxes: sq.boxes.filter(b => b.phenotype === 'green').length, pYy: X.phenotype('seedColour', 'Yy') },
      gam: [g1, g2], cap, low,
      kids: [kid(cap, g2.find(a => a !== a.toUpperCase())), kid(low, g2.find(a => a !== a.toUpperCase()))],   // chorus 1: Y + y; chorus 2: y + y
      grow: X.grow('Yy', 'Yy', 8, 104),                   // "every seed's a new draw": a seeded row of 8
      pct: X.percent('Yy', 'Yy', { kind: 'pheno', value: 'green' }),
      mendel: { d: M.d, r: M.r, ratio: M.ratio, dom: M.dominant, rec: M.recessive },
    };
    return NUMS;
  }

  /* ---------------- the plan: acts ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'verse1', at: -1, draw: actVerse1 },
      { id: 'chorus1', at: S.W(4, 'Big') - 0.3, draw: actChorus, c0: 4 },
      { id: 'verse2', at: b(8), draw: actVerse2 },
      { id: 'bridge', at: b(12), draw: actBridge },
      { id: 'chorus2', at: S.W(14, 'Big') - 0.3, draw: actChorus, c0: 14 },
      { id: 'outro', at: b(18), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // pods on the vine: each chorus "pair" and the bridge's "Mendel"; flowers after the outro's first "gone"
    const pods = [S.W(7, 'pair'), S.W(13, 'Mendel'), S.W(17, 'pair')];
    const tBloom = S.W(18, 'gone');
    return { acts, ticks: acts.slice(1).map(a => a.at), pods, tBloom, tStamp: S.E(19) + 0.9, grow: S.dur - 2.5 };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  const inkA = (pts, o = {}) => ink(pts, { ...o, alpha: (o.alpha ?? 1) * g.globalAlpha });
  const tile = (l, x, y, size, o = {}) => window.Plate.tile(l, x, y, size, o);
  const note = (str, x, y, size, t0, o = {}) => hand(str, x, y, size, t0, { color: PC().ink, dur: 0.4, ...o });
  /* a tile that travels a → b (an arc) over [t0, t0 + d] */
  function fly(a, b, t, t0, d = 0.5, lift = 90) {
    const u = E.inOutCubic(P(t, t0, t0 + d));
    return [lerp(a[0], b[0], u), lerp(a[1], b[1], u) - lift * Math.sin(Math.PI * u)];
  }
  const bounce = (t, t0, k = 6, a = 0.14) => (t >= t0 ? 1 + a * Math.exp(-(t - t0) * k) * Math.abs(Math.cos((t - t0) * 9)) : 1);

  /* ---------------- the stage: paper, bench, the pea vine ---------------- */
  const VN = 240, VX0 = 150, VX1 = 1830;
  const VINE = [];
  for (let i = 0; i <= VN; i++) { const u = i / VN; VINE.push([VX0 + (VX1 - VX0) * u, BENCH_Y - 14 - 16 * Math.sin(u * Math.PI * 9)]); }
  const vinePt = u => { const f = clamp(u) * VN, i = Math.min(VN - 1, Math.floor(f)), k = f - i; return [lerp(VINE[i][0], VINE[i + 1][0], k), lerp(VINE[i][1], VINE[i + 1][1], k)]; };
  const LEAF = Object.assign([[0, 0], [22, -17], [52, -15], [70, 0], [48, 12], [20, 11]], { k: 'song:leaf' });
  const POT = Object.assign([[-62, -44], [62, -44], [48, 44], [-48, 44]], { k: 'song:pot' });
  /* the tip steps forward on the beat (it moves in the first ~45 % of each beat, then rests); a leaf every 2 beats */
  function vineAt(S, p, t) {
    let tq = t;
    if (t > S.off) { const bp = S.beatPos(t), fl = Math.floor(bp); tq = S.off + (fl + E.inOutSine(clamp((bp - fl) * 2.2))) * S.bt; }
    const tip = clamp(tq / p.grow), leaves = t < S.off ? 0 : Math.floor(S.beatPos(t) / 2) + 1;
    return { tip, leaves };
  }
  function drawVine(S, p, t) {
    const C = PC(), V = vineAt(S, p, t);
    // the potted pea plant the vine grows from
    if (IMG.pea_plant) cut('pea_plant', 96, BENCH_Y + 20, 300, { ax: 0.5, ay: 1, seed: 931, depth: 1, boil: false, rot: Math.sin(t * 0.8) * 0.01 });
    paperPiece(POT, 96, BENCH_Y + 40, '#b5623a', { seed: 932, depth: 1.1, boil: false });
    paperRect(96, BENCH_Y - 2, 138, 16, '#9a4f2c', { seed: 933, depth: 0.4, boil: false });
    if (V.tip <= 0) return V;
    // the stem up to the tip
    const n = Math.max(2, Math.floor(V.tip * VN) + 1), pts = VINE.slice(0, n);
    pts.push(vinePt(V.tip));
    inkA(pts, { w: 6, color: C.leafDark, seed: 941, passes: 1, step: 22, amp: 0.6 });
    // a leaf every 2 beats, up and down in turn (the newest one springs); a tendril curl on every other up leaf
    for (let k = 0; k < V.leaves; k++) {
      const tb = S.off + k * 2 * S.bt, u = clamp(tb / p.grow), [x, y] = vinePt(u), up = k % 2 === 0;
      const s = pop(T, tb, 0.35, 2.4); if (s <= 0) continue;
      g.save(); g.translate(x, y); g.rotate((up ? -0.95 : 0.75) + Math.sin(t * 1.3 + k) * 0.04); g.scale(s, s * (up ? 1 : -1));
      paperPiece(LEAF, 0, 0, k % 3 ? C.leaf : C.greenPea, { seed: 950 + (k % 7), depth: 0.5, boil: false });
      g.restore();
      if (up && k % 4 === 2) {
        const cx = x - 6, cy = y - 34, curl = [[x, y - 4]];
        for (let j = 0; j <= 12; j++) { const a = Math.PI / 2 + j / 12 * TAU * 1.4; curl.push([cx + Math.cos(a) * (14 - j), cy - Math.sin(a) * (14 - j)]); }
        inkA(curl, { w: 2.5, color: C.leafDark, seed: 960 + (k % 5), passes: 1, step: 6, amp: 0.3, prog: clamp((t - tb) / 0.5) });
      }
    }
    // pods on the chorus' "pair" and on "Mendel"
    p.pods.forEach((tp, j) => {
      if (t < tp) return;
      const [x, y] = vinePt(clamp(tp / p.grow));
      window.Plate.pheno('pod', 'inflated', x - 18, y + 30, 20, { seed: 970 + j, s: pop(T, tp, 0.35, 2.2) });
    });
    // flowers at the end
    if (t >= p.tBloom) for (let j = 0; j < 5; j++) {
      const t0 = p.tBloom + j * 0.3, [x, y] = vinePt(0.14 + j * 0.19), s = pop(T, t0, 0.4, 2);
      if (s <= 0) continue;
      if (IMG.pea_flower) cut('pea_flower', x, y + 4, 92, { ax: 0.5, ay: 1, seed: 980 + j, depth: 0.6, boil: false, s, rot: (j % 2 ? 0.1 : -0.1) });
      else window.Plate.pheno('flower', 'violet', x, y - 30, 24, { seed: 980 + j, s });
    }
    return V;
  }
  function backdrop(S, p, t, docked) {
    const C = PC();
    g.fillStyle = paperTile(C.paper, 4); g.fillRect(0, 0, 1920, 1080);
    if (IMG.tex_paper) { g.save(); g.globalAlpha = 0.35; g.drawImage(IMG.tex_paper, 0, 0, 1920, 1080); g.restore(); }
    g.save(); g.strokeStyle = 'rgba(80,60,35,0.4)'; g.lineWidth = 3; g.strokeRect(24, 24, 1872, 1032); g.lineWidth = 1.2; g.strokeRect(34, 34, 1852, 1012); g.restore();
    // the potting bench
    paperRect(960, BENCH_Y + 34, 2000, 46, C.wood, { seed: 921, depth: 1.2, boil: false });
    if (docked) paperRect(960, 1010, 2080, 150, C.cream, { seed: 922, torn: true, depth: 0.8, boil: false });
    drawVine(S, p, t);
  }

  /* ======================================================================
     the acts
     ====================================================================== */
  /* verse 1: the family card (SIMPLIFIED MODEL): one allele from each parent */
  const FAMX = [520, 830, 1180], FAMY = 300;
  function actVerse1(S, t) {
    const C = PC(), n = nums().eye;
    const tBrown = S.W(0, 'browneyed'), tBlue = S.W(0, 'blueeyed'), tAsk = S.W(1, 'Asking'), tHid = S.W(1, 'hidden');
    const tOne = S.W(2, 'One'), tMom = S.W(2, 'mother'), tDad = S.W(2, 'dad'), tPair = S.W(3, 'pair'), tTraits = S.W(3, 'traits');
    const cardS = pop(T, -0.4, 0.4, 2);
    g.save(); g.translate(850, 410); g.scale(cardS, cardS); g.translate(-850, -410);
    paperRect(850, 410, 1100, 500, C.cream, { seed: 201, depth: 1.3, boil: false, rot: -0.008 });
    window.Plate.label('SIMPLIFIED MODEL', 850, 172, 30, { seed: 202, rot: -0.01 });
    const who = ['Mom', 'Dad', 'Lily'], eye = [n.parent, n.parent, n.lilyPheno], at = [tBrown, tBrown + 0.18, tBlue];
    for (let k = 0; k < 3; k++) {
      const x = FAMX[k];
      paperRect(x, FAMY, 170, 150, C.label, { seed: 210 + k, depth: 0.6, boil: false, rot: (k - 1) * 0.02 });
      g.save(); g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 2; g.strokeRect(x - 72, FAMY - 62, 144, 124); g.restore();
      const es = pop(T, at[k], 0.35, 2.2);
      if (es > 0) window.Plate.pheno('eye', eye[k], x, FAMY, 44, { seed: 220 + k, s: es * bounce(t, at[k]) });
      note(who[k], x, FAMY + 104, 40, -1e6, { dur: 0.01, color: k === 2 ? C.leafDark : C.ink, seed: 230 + k });
    }
    if (t >= tAsk) {
      g.save(); g.translate(FAMX[2] + 118, FAMY - 60); g.rotate(0.12); g.scale(bounce(t, tHid, 5, 0.2), bounce(t, tHid, 5, 0.2));
      window.Plate.woodtype('?', 0, 0, 64, { t0: tAsk, seed: 240, color: C.label });
      g.restore();
    }
    // the alleles: Mom and Dad each carry B b; one b from each slides down into Lily's pair
    const TY = 470, TS = 62, pair = (x) => [[x - 38, TY], [x + 38, TY]];
    const lilySlot = pair(FAMX[2]);
    if (t >= tOne - 0.1) {
      [n.p1, n.p2].forEach((gt, k) => {
        const slots = pair(FAMX[k]), give = gt.indexOf(n.lilyFrom[k]), tGo = k ? tDad : tMom;
        [...gt].forEach((l, j) => {
          const s = pop(T, tOne - 0.1 + k * 0.12 + j * 0.06, 0.3, 2.2);
          let [x, y] = slots[j];
          if (j === give && t >= tGo) [x, y] = fly(slots[j], lilySlot[k], t, tGo, 0.55, 110);
          if (j === give && t >= tGo) tile('x', slots[j][0], slots[j][1], TS, { state: 'ghost', seed: 250 + k * 2 + j });
          tile(l, x, y, TS, { s, seed: 260 + k * 2 + j, rot: (j ? 0.04 : -0.03) });
        });
      });
      if (t < tMom + 0.55) lilySlot.forEach((q, j) => tile('x', q[0], q[1], TS, { state: 'ghost', seed: 270 + j }));
    }
    if (t >= tPair) {
      inkA([[FAMX[2] - 84, TY + 44], [FAMX[2] - 84, TY + 58], [FAMX[2] + 84, TY + 58], [FAMX[2] + 84, TY + 44]], { w: 4, color: C.leafDark, seed: 280, prog: E.outCubic(P(t, tPair, tPair + 0.35)), passes: 1, step: 12 });
      note('a gene pair: one allele from each parent', 850, TY + 100, 38, tPair, { seed: 281 });
    }
    if (t >= tTraits) note(`Lily: ${n.lily} = ${n.lilyPheno} eyes (in this simplified model)`, 850, TY + 150, 36, tTraits, { seed: 282, color: C.leafDark });
    g.restore();
    // Lily herself, stage right
    const ls = pop(T, -0.2, 0.5, 1.6);
    if (ls > 0) Puppet.draw('lily', 1680, BENCH_Y + 10, 560, { facing: -1, t, env: 0, s: ls * (t >= tBlue && t < tBlue + 1 ? bounce(t, tBlue, 5, 0.05) : 1) });
  }

  /* the chorus: the big Y hides the little y (still there); one from each parent */
  const YB = [960, 380], Y_OUT = [1115, 455];
  function hideAndSeek(S, t, c0) {
    const C = PC(), n = nums();
    const tBig = S.W(c0, 'Big'), tHides = S.W(c0, 'hides'), tLittle = S.W(c0, 'little');
    const tStill = S.W(c0 + 1, 'still'), tThere = S.W(c0 + 1, 'there');
    const tHid = S.W(c0 + 2, 'Hidden'), tNot = S.W(c0 + 2, 'not'), tGone = S.W(c0 + 2, 'gone'), tRide = S.W(c0 + 2, 'riding');
    const ride = t >= tRide ? 330 * E.inOutSine(P(t, tRide, S.E(c0 + 2) + 0.4)) : 0, bob = t >= tRide ? 10 * S.pulse(t, 6) : 0;
    // the pea pod they ride on
    if (t >= tRide - 0.3 && IMG.pea_pod) cut('pea_pod', YB[0] + 60 + ride, YB[1] + 150 - bob * 0.5, 120, { seed: 301, depth: 1, boil: false, s: pop(T, tRide - 0.3, 0.35, 2), rot: -0.03 + Math.sin(t * 3) * 0.02 });
    // little y: centre, then under the big Y, then peeking out on "still"
    let y = [YB[0] + 170, YB[1] + 20];
    if (t >= tHides) y = [YB[0] + 30, YB[1] + 18];
    if (t >= tStill) { const u = E.outBack(P(t, tStill, tStill + 0.45), 1.8); y = [lerp(YB[0] + 30, Y_OUT[0], u), lerp(YB[1] + 18, Y_OUT[1], u)]; }
    const yRot = t >= tStill ? 0.16 + 0.06 * Math.sin((t - tStill) * 7) * Math.exp(-(t - tStill) * 1.5) : 0.03;
    tile(n.low, y[0] + ride, y[1] - bob, 150, { s: pop(T, S.S(c0) - 0.35, 0.3, 2.2), seed: 311, rot: yRot });
    // big Y: in from the left on "Big", over the little one on "hides"
    let Y = [YB[0] - 250, YB[1]];
    if (t >= tHides) { const u = E.inOutCubic(P(t, tHides, tHides + 0.4)); Y = [lerp(YB[0] - 250, YB[0], u), YB[1] - 12 * Math.sin(Math.PI * u)]; }
    tile(n.cap, Y[0] + ride, Y[1] - bob, 190, { s: pop(T, tBig - 0.1, 0.3, 2.2) * bounce(t, tBig, 7, 0.08), seed: 312, rot: -0.03 });
    const words = 1 - P(t, tHid - 0.3, tHid);
    withAlpha(words, () => {
      if (t >= tBig) note('big Y (dominant)', Y[0], YB[1] - 150, 38, tBig, { seed: 321 });
      if (t >= tLittle && t < tStill) note('little y: under there', YB[0] + 30, YB[1] + 170, 38, tLittle, { seed: 322 });
      if (t >= tStill) {
        note('still there!', 1340, 560, 46, tStill, { seed: 323, color: C.red });
        inkA([[1270, 540], [1215, 505], [1190, 490]], { w: 4, color: C.red, seed: 324, prog: E.outCubic(P(t, tStill + 0.1, tStill + 0.4)), passes: 1, step: 10 });
      }
      if (t >= tThere) {
        window.Plate.pheno('pea', n.yy.pYy, 1390, 300, 48, { seed: 325, s: pop(T, tThere, 0.35, 2.2) });
        note(`Yy looks ${n.yy.pYy}`, 1390, 390, 38, tThere, { seed: 326 });
      }
    });
    hookRow([['HIDDEN', tHid], ['NOT', tNot], ['GONE', tGone]], t, 176, 74, 331);
    if (t >= tGone + 0.2 && t >= tRide) note('riding along, one generation to the next', YB[0] + 90 + ride, YB[1] + 240, 34, tRide + 0.3, { seed: 333, color: C.leafDark });
  }
  /* HIDDEN · NOT · GONE: one ransom row, each word popping in on its sung word */
  function hookRow(words, t, y, size, seed) {
    const gap = size * 0.5, ws = words.map(([w], k) => ransomW(w, size, seed + k)), tot = ws.reduce((a, b) => a + b, 0) + gap * (words.length - 1);
    let x = 960 - tot / 2;
    words.forEach(([w, t0], k) => {
      const cx = x + ws[k] / 2; x += ws[k] + gap;
      if (t < t0) return;
      const s = 1 + 0.12 * Math.exp(-(t - t0) * 7);
      g.save(); g.translate(cx, y); g.scale(s, s); ransom(w, 0, 0, size, t0, { seed: seed + k, stagger: 0.03 }); g.restore();
    });
  }
  const PARX = [560, 1360], PARY = 300, KIDY = 560, KIDX = [900, 1020];
  function oneFromEach(S, t, c0, kidIx) {
    const C = PC(), n = nums(), kid = n.kids[kidIx], l = c0 + 3;
    const tOne = S.W(l, 'One'), tPar = S.W(l, 'parent'), tPair = S.W(l, 'pair');
    const tGo = [tOne, tPar];
    [0, 1].forEach(k => {
      const gam = n.gam[k], give = gam.indexOf(kid.from[k]), split = E.outCubic(P(t, tGo[k] - 0.15, tGo[k] + 0.2)) * 36;
      note(`parent ${k + 1}: ${gam.join('')}`, PARX[k], PARY - 104, 38, S.S(l) - 0.3, { seed: 340 + k });
      gam.forEach((a, j) => {
        let p = [PARX[k] + (j ? 1 : -1) * (46 + split), PARY];
        if (j === give && t >= tGo[k]) p = fly(p, [KIDX[k], KIDY], t, tGo[k], 0.5, 60);
        tile(a, p[0], p[1], 84, { s: pop(T, S.S(l) - 0.3 + j * 0.06, 0.3, 2.2), seed: 350 + k * 2 + j, rot: j ? 0.04 : -0.03 });
      });
    });
    if (t < tPar + 0.5) KIDX.forEach((x, j) => { if (t < tGo[j] + 0.5) tile('x', x, KIDY, 84, { state: 'ghost', seed: 360 + j }); });
    if (t >= tPair) {
      twine(KIDX[0] - 60, KIDY + 56, KIDX[1] + 60, KIDY + 56, tPair);
      window.Plate.pheno('pea', kid.phenotype, 1180, KIDY, 44, { seed: 364 + kidIx, s: pop(T, tPair, 0.35, 2.2) });
      note(`${kid.genotype} = ${kid.phenotype}`, 1180, KIDY + 76, 38, tPair, { seed: 366, color: kid.phenotype === 'green' ? C.leafDark : YELLOW_TEXT });
      note('one from each parent', 960, KIDY + 124, 40, tPair + 0.2, { seed: 367 });
    }
  }
  function twine(x0, y0, x1, y1, t0) {
    if (T - t0 > 0.15 && g.globalAlpha > 0.5) window.Plate.twine(x0, y0, x1, y1, { sag: 18, seed: 368 });   // ink() sets its own alpha: shown only while the act is up
  }
  function actChorus(S, t, A) {
    const c0 = A.c0, L3 = S.S(c0 + 3);
    withAlpha(1 - P(t, L3 - 0.45, L3 - 0.1), () => hideAndSeek(S, t, c0));
    withAlpha(P(t, L3 - 0.35, L3), () => oneFromEach(S, t, c0, c0 === 4 ? 0 : 1));
  }

  /* verse 2: the seed tray fills; 1 in 4 green, 3 yellow; each seed a new draw; 25% */
  const TR = { x: 640, y: 440, cell: 150 };
  function actVerse2(S, t) {
    const C = PC(), n = nums(), Y = n.yy, Pl = window.Plate;
    const tDraw = S.W(8, 'Draw'), tBox = ['four', 'boxes', 'to', 'fill'].map(w => S.W(8, w));
    const tGreen = S.W(9, 'green'), tThree = S.W(9, 'three'), tYel = S.W(9, 'yellow');
    const tEvery = S.W(10, 'Every'), tDrawN = S.W(10, 'draw'), tScore = S.W(10, 'score');
    const tTw = S.W(11, 'twentyfive'), tSame = S.W(11, 'same');
    const s = pop(T, tDraw - 0.15, 0.35, 2);
    const Lz = Pl.tray(TR.x, TR.y, TR.cell, { top: Y.top, side: Y.side, boxes: [null, null, null, null], seed: 401, s, label: `${Y.p1} × ${Y.p2}` });
    if (s < 0.99) return;
    // each box fills on its word (the tray's own box drawing, popped one by one)
    const cell = TR.cell, ts = cell * 0.27, ty = -cell * 0.17;
    Lz.boxes.forEach((b, i) => {
      const sc = pop(T, tBox[i], 0.3, 2.2); if (sc <= 0) return;
      const gt = Y.boxes[i];
      g.save(); g.translate(b.x, b.y); g.scale(sc, sc);
      tile(gt[0], -ts * 0.56, ty, ts, { seed: 471 + i * 2, rot: -0.03 });
      tile(gt[1], ts * 0.56, ty, ts, { seed: 472 + i * 2, rot: 0.03 });
      Pl.pheno('seedColour', Y.pheno[i], 0, cell * 0.2, cell * 0.12, { seed: 481 + i });
      g.restore();
    });
    // one in four green (the ring), three yellow (ticks)
    const RX = 1010;
    Lz.boxes.forEach((b, i) => {
      if (Y.pheno[i] === 'green' && t >= tGreen) {
        const pu = t >= tSame ? 1 + 0.08 * Math.exp(-(t - tSame) * 4) : 1;
        inkCircle(b.x, b.y, cell * 0.47 * pu, { alpha: g.globalAlpha, w: 7, color: C.leafDark, seed: 491, passes: 2, prog: E.outCubic(P(t, tGreen, tGreen + 0.4)) });
      }
      if (Y.pheno[i] === 'yellow' && t >= tThree) {
        const t0 = tThree + 0.12 * i;
        inkA([[b.x + cell * 0.2, b.y + cell * 0.3], [b.x + cell * 0.28, b.y + cell * 0.38], [b.x + cell * 0.42, b.y + cell * 0.18]], { w: 6, color: YELLOW_TEXT, seed: 495 + i, passes: 1, step: 10, prog: E.outCubic(P(t, t0, t0 + 0.25)) });
      }
    });
    const lA = 1 - P(t, tEvery - 0.3, tEvery);
    withAlpha(lA, () => {
      if (t >= tGreen) note(`${Y.greenBoxes} in 4 green = ${Y.green}%`, RX, 330, 50, tGreen, { seed: 501, align: 'left', color: C.leafDark });
      if (t >= tThree) note(`3 in 4 yellow = ${Y.yellow}%`, RX, 420, 50, tYel - 0.2, { seed: 502, align: 'left', color: YELLOW_TEXT });
    });
    // every seed's a new draw: the pod and a simulated row of 8 seeds (Cross.grow), one per half-beat
    if (t >= tEvery - 0.2) {
      const PX = 1330, kids = n.grow.kids;
      if (IMG.pea_pod) cut('pea_pod', PX, 250, 110, { seed: 511, depth: 1, boil: false, s: pop(T, tEvery - 0.2, 0.35, 2), rot: 0.04 });
      kids.forEach((k, j) => {
        const t0 = tEvery + j * S.bt * 0.5, sp = pop(T, t0, 0.3, 2.4); if (sp <= 0) return;
        Pl.pheno('seedColour', Cross.phenotype('seedColour', k), PX - 196 + j * 56, 360, 22, { seed: 520 + j, s: sp });
      });
      if (t >= tDrawN) note('each seed: a brand-new draw (a simulated row)', PX, 422, 34, tDrawN, { seed: 531 });
      const sa = 1 - P(t, tTw + 0.8, tTw + 1.2);
      if (t >= tScore) withAlpha(sa, () => {
        const x0 = PX - 250, y0 = 530;
        for (let j = 0; j < 4; j++) inkA([[x0 + j * 22, y0 - 30], [x0 + j * 22 + 3, y0 + 30]], { w: 5, color: C.ink, seed: 541 + j, passes: 1, step: 20, prog: clamp((t - tScore - j * 0.05) / 0.12) });
        inkA([[x0 - 16, y0 + 24], [x0 + 84, y0 - 24]], { w: 6, color: C.ink, seed: 545, passes: 1, step: 20, prog: clamp((t - tScore - 0.25) / 0.15) });
        inkA([[x0 - 34, y0 - 44], [x0 + 104, y0 + 44]], { w: 8, color: C.red, seed: 546, passes: 2, step: 20, prog: clamp((t - tScore - 0.45) / 0.2) });
        note('the pod keeps no score', x0 + 50, y0 + 70, 34, tScore + 0.3, { seed: 547, color: C.red });
      });
      if (t >= tTw) {
        g.save(); g.translate(PX + 110, 560); g.scale(bounce(t, tTw, 5, 0.1), bounce(t, tTw, 5, 0.1));
        window.Plate.woodtype(`${n.pct}%`, 0, 0, 90, { t0: tTw, seed: 551, color: C.label, stagger: 0.08 });
        g.restore();
        note('green, for every single seed', PX + 110, 650, 38, tTw + 0.4, { seed: 552, color: C.leafDark });
      }
    }
  }

  /* the bridge: eye color is a team of genes (simplified); peas keep it to one; Mendel's real counts */
  const TEAM = [['A', 'a'], ['D', 'd'], ['E', 'e'], ['G', 'g'], ['H', 'h'], ['K', 'k']];
  function actBridge(S, t) {
    const C = PC(), n = nums(), M = n.mendel, Pl = window.Plate;
    const tEye = S.W(12, 'Eye'), tTeam = S.W(12, 'team'), tGenes = S.W(12, 'genes'), tNot = S.W(12, 'not');
    const tPeas = S.W(13, 'peas'), tSimple = S.W(13, 'simple'), tMendel = S.W(13, 'Mendel');
    if (t >= tEye - 0.1) Pl.woodtype('EYE COLOR = A TEAM OF GENES', 960, 196, 50, { t0: tEye - 0.1, seed: 601, stagger: 0.03, color: C.label });
    const EX = 600, EY = 480;
    if (t >= tEye) {
      paperCircle(EX, EY, 205, C.cream, { seed: 602, depth: 1, boil: false });
      Pl.pheno('eye', 'blue', EX - 34, EY, 40, { seed: 603, s: pop(T, tEye, 0.35, 2) });
      Pl.pheno('eye', 'brown', EX + 44, EY + 8, 40, { seed: 604, s: pop(T, tEye + 0.15, 0.35, 2) });
    }
    TEAM.forEach((pr, k) => {
      const t0 = tTeam + k * 0.16; if (t < t0) return;
      const a = -Math.PI / 2 + k / TEAM.length * TAU, x = EX + Math.cos(a) * 165, y = EY + Math.sin(a) * 150, sp = pop(T, t0, 0.3, 2.2);
      pr.forEach((l, j) => tile(l, x + (j ? 21 : -21), y, 40, { s: sp * (t >= tGenes ? bounce(t, tGenes + k * 0.05, 6, 0.1) : 1), seed: 611 + k * 2 + j, rot: (j ? 0.05 : -0.05) }));
    });
    if (t >= tGenes) note('(a simplified picture: many genes; the letters are made up)', 800, 252, 32, tGenes, { seed: 631, color: C.leafDark });
    if (t >= tNot) note('not ONE gene alone', EX + 225, EY + 110, 40, tNot, { seed: 632, color: C.red, align: 'left' });
    // the peas: one gene
    const PX = 1350;
    if (t >= tPeas - 0.2) {
      if (IMG.pea_pod) cut('pea_pod', PX, 300, 110, { seed: 641, depth: 1, boil: false, s: pop(T, tPeas - 0.2, 0.35, 2), rot: -0.04 });
      if (t >= tSimple) {
        tile(n.cap, PX - 60, 415, 84, { s: pop(T, tSimple, 0.3, 2.2), seed: 642, rot: -0.04 });
        tile(n.low, PX + 60, 415, 84, { s: pop(T, tSimple + 0.1, 0.3, 2.2), seed: 643, rot: 0.04 });
        note('peas: ONE gene, Y or y', PX, 492, 40, tSimple + 0.1, { seed: 644 });
      }
      if (t >= tMendel) {
        Pl.label('MENDEL, 1865', PX, 560, 30, { t0: tMendel, seed: 645, rot: 0.01, s: pop(T, tMendel, 0.3, 2) });
        note(`${fmtInt(M.d)} ${M.dom} : ${fmtInt(M.r)} ${M.rec}  (about 3 : 1)`, PX, 622, 34, tMendel + 0.3, { seed: 646 });
      }
    }
  }
  const fmtInt = v => Math.round(v).toLocaleString('en-US');

  /* the outro: hidden, not gone (twice); flowers; INHERITED */
  function actOutro(S, t, A) {
    const C = PC(), n = nums();
    const hid = [S.W(18, 'Hidden'), S.W(19, 'Hidden')], gone = [S.W(18, 'gone'), S.W(19, 'gone')];
    // which beat of the hide-and-seek: covered after each "Hidden", out after each "gone"
    let out = 1, tOut = -1e6, tIn = -1e6;
    for (let k = 0; k < 2; k++) { if (t >= hid[k]) { out = 0; tIn = hid[k]; } if (t >= gone[k]) { out = 1; tOut = gone[k]; } }
    const u = out ? E.outBack(P(t, tOut, tOut + 0.45), 1.8) : 1 - E.inOutCubic(P(t, tIn, tIn + 0.35));
    const tS = A.tStamp, cx = 960 - 230 * E.inOutCubic(P(t, tS - 0.5, tS - 0.05)), cy = 400;
    tile(n.low, lerp(cx + 30, cx + 160, u), lerp(cy + 18, cy + 80, u), 150, { s: pop(T, A.at, 0.3, 2), seed: 701, rot: 0.03 + 0.16 * u + (t >= gone[1] ? 0.1 * Math.sin((t - gone[1]) * 8) * Math.exp(-(t - gone[1]) * 1.2) : 0) });
    tile(n.cap, cx, cy, 190, { s: pop(T, A.at + 0.1, 0.3, 2), seed: 702, rot: -0.03 });
    hookRow([['HIDDEN', hid[0]], ['NOT', S.W(18, 'not')], ['GONE', gone[0]]], t, 176, 74, 711);
    if (t >= gone[0]) note('the little y is still there', cx + 220, cy + 190, 40, gone[0] + 0.2, { seed: 713, color: C.leafDark });
    if (t >= tS) {
      UI.stamp('INHERITED', 1330, 450, { rot: -0.12, s: pop(T, tS, 0.3, 2.2) * 1.1, color: C.red });
      window.Plate.seal(1640, 560, 58, { text: 'y', seed: 721, s: pop(T, tS + 0.25, 0.35, 2.4), rot: 0.2 });
    }
  }

  function drawStage(S, p, t, docked) {
    backdrop(S, p, t, docked);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); p.acts[i].draw(S, t, { ...p.acts[i], tStamp: p.tStamp }); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
  }

  /* ---------------- karaoke ---------------- */
  const HOOK = ['hidden', 'not', 'gone'];
  const WORD_COL = () => { const C = PC(); return { green: C.leafDark, yellow: YELLOW_TEXT, blueeyed: C.blueEye, browneyed: C.brownEye, mendel: C.leafDark, punnett: C.woodDark }; };
  const wordColor = w => WORD_COL()[normW(w)] || null;
  function hookIdx(item) {
    const ws = item.words, out = new Set();
    for (let k = 0; k + HOOK.length <= ws.length; k++) if (HOOK.every((w, j) => normW(ws[k + j].w) === w)) HOOK.forEach((_, j) => out.add(k + j));
    return out;
  }
  function tokens(item) {
    const ws = item.words, hk = hookIdx(item), toks = [];
    for (let k = 0; k < ws.length; k++) {
      if (hk.has(k) && !hk.has(k - 1)) {
        const run = ws.slice(k, k + HOOK.length), last = run[run.length - 1];
        const trail = (last.w.match(/[,.!?]+$/) || [''])[0];
        toks.push({ text: run.map(w => w.w.replace(/[,.!?]+/g, '')).join(' ').toUpperCase(), s: run[0].s, e: last.e, ransom: true, trail });
        k += HOOK.length - 1; continue;
      }
      toks.push({ text: ws[k].w, s: ws[k].s, e: ws[k].e, col: wordColor(ws[k].w) });
    }
    return toks;
  }
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const toks = tokens(item), f = `${size}px ${WOOD}`, space = measure(' ', f) + size * 0.08, rs = size * 0.62;
    let x = 0;
    toks.forEach((tk, j) => {
      tk.seed = 400 + item.i * 13 + j * 5;
      if (tk.ransom) {
        tk.w = ransomW(tk.text, rs, tk.seed); tk.tw = tk.trail ? measure(tk.trail, f) : 0;
        tk.x = x + size * 0.1; x += tk.w + tk.tw + size * 0.2 + space;
      } else { tk.w = measure(tk.text, f); tk.x = x; x += tk.w + space; }
    });
    const out = { toks, width: x - space, size, rs };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1780) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 960 - Lo.width / 2, C = PC();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 36;
    const f = `${size}px ${WOOD}`;
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (tk.ransom) {
        if (!sung) withAlpha(0.3 * a, () => { g.font = `${Math.round(size * 0.8)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.text, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.06 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.text, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.text.length), 0.02, 0.06) });
          g.restore();
        }
        if (tk.trail && sung) { g.save(); g.font = f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 6, yy); g.restore(); }
        return;
      }
      if (active) paperRect(x + tk.w / 2, yy + size * 0.04, tk.w + 24, size * 1.08, PAL.mustard, { seed: tk.seed, depth: 0.5, boil: false, s: 1 + 0.06 * Math.exp(-(t - tk.s) * 10) });
      g.save(); g.font = f; g.textAlign = 'left'; g.textBaseline = 'middle';
      if (!sung) { g.globalAlpha *= 0.3 * a; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); }
      else {
        const p = E.outQuad(P(t, tk.s, tk.s + clamp(tk.e - tk.s, 0.08, 0.3)));
        if (p < 1) { g.save(); g.globalAlpha *= 0.3; g.fillStyle = C.ink; g.fillText(tk.text, x, yy); g.restore(); g.beginPath(); g.rect(x - 4, yy - size, (tk.w + 8) * p, size * 2); g.clip(); }
        g.fillStyle = tk.col || C.ink; g.fillText(tk.text, x, yy);
      }
      g.restore();
    });
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been sung */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  const endNote = () => ((Game.content.song && Game.content.song.hook) || [])[1] || "But the little one's still there";
  function band(song, t) {
    const C = PC();
    paperRect(960, BAND_Y + 140, 2040, 290, C.cream, { seed: 77, torn: true, depth: 1.8, boil: false });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) { g.save(); g.font = `28px ${TYPE}`; g.fillStyle = 'rgba(47,82,38,0.7)'; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(sec.toLowerCase(), 70, BAND_Y + 36); g.restore(); }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const nt = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) nt(next.text, C.leafDark, 0.65);
    else if (song.items.length && t > song.items[song.items.length - 1].e) nt(endNote(), C.leafDark, 0.8);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      g.save(); g.font = `72px 'Segoe UI Symbol', 'DejaVu Sans', sans-serif`; g.fillStyle = C.leafDark; g.textAlign = 'center'; g.textBaseline = 'middle';
      ['♪', '♫', '♪'].forEach((nn, i) => g.fillText(nn, 860 + i * 100, CUR_Y - 24 * pu * (i === b ? 1 : 0.15)));
      g.restore();
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class SongScene extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      try { nums(); } catch (e) { this.err = 'cross model: ' + e.message; console.warn('[song]', this.err); }
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[song] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => { if (!this.gone && !this.frozen && !this.startedOnce && this.song) this.play(0); });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    vineAt(t) { return this.plan ? vineAt(this.song, this.plan, t) : null; }

    update(dt) {
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) Audio.play('stamp', { gain: 0.6 });
      if (this.lastT < this.plan.tBloom && now >= this.plan.tBloom && Audio.has('pod_pop')) Audio.play('pod_pop', { gain: 0.35 });
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = (Game.content.song && Game.content.song.title) || 'Hidden in the Pod';
      if (!this.song || this.err) {
        g.fillStyle = paperTile(PC().paper, 4); g.fillRect(0, 0, 1920, 1080);
        ransom('HIDDEN IN THE POD', 960, 400, 100, this.enteredAt + 0.2, { seed: 42 });
        const msg = this.err ? 'the song is missing (' + this.err + ')' : 'tuning the guitar…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: PC().leafDark, seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('sg-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t, docked);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) {
          const hk = hookIdx(cur);
          this._dk = { key, words: cur.words.map((w, j) => ({ w: w.w, s: w.s, e: w.e, hook: hk.has(j) })) };
        }
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? endNote() : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'loading the song…' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('sg-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: PC().leafDark, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('sg-finish', [{ label: 'Finish', color: PC().red, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'song position', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the song from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused ▶: a dim wash and a mustard paper disc */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(27,21,18,0.28)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, PAL.mustard, { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = PAL.ink; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = PC();
      ransom('HIDDEN IN THE POD', 40, 62, 36, t0 + 0.1, { align: 'left', seed: 61 });
      hand('♪ ' + CREDIT, 44, 118, 28, t0 + 0.4, { align: 'left', color: C.leafDark, seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('sg-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('sg-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('sg-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: PAL.mustard })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('sg-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(43,36,32,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.leaf; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.woodDark; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.leafDark, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: PAL.cream, outline: 'rgba(31,59,42,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('loading the song…', 960, 590, 48, this.enteredAt + 0.3, { color: C.leafDark, seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('sg-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('sg-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: C.leafDark, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('sg-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.red, size: 52 })) this.done();
      }
    }
  }
  SongScene.STEP = STEP;
  SongScene.TAKE = TAKE_NAME;
  SongScene.Song = Song;
  SongScene.plan = plan;
  SongScene.nums = nums;
  Levels.song = SongScene;
})();
